import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Field, SectionTitle } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const PREFS = [["email_invites", "Trip invitation emails"], ["email_announcements", "Announcement emails from organizers"],
  ["email_payment_reminders", "Payment reminder emails"], ["email_itinerary", "Itinerary change emails (in-app alerts always on)"]];

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(user.name);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [confirm, setConfirm] = useState("");
  const save = async (body) => {
    try { const { data } = await api.patch("/auth/me", body); setUser(data); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", pw); toast.success("Password updated"); setPw({ current_password: "", new_password: "" }); } catch (er) { toast.error(errMsg(er)); }
  };
  const exportData = async () => {
    const { data } = await api.get("/account/export");
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url; a.download = "triply-my-data.json"; a.click();
  };
  const del = async () => {
    try { await api.post("/account/delete", { confirm }); await logout(); toast.success("Your account was deleted"); nav("/"); } catch (e) { toast.error(errMsg(e)); }
  };
  const prefs = user.notification_prefs || {};
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <SectionTitle title="Account settings" sub={user.email} />
      <div className="card space-y-4 p-6">
        <h3 className="font-semibold">Profile</h3>
        <div className="flex gap-3"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" /><Button className="rounded-full" onClick={() => save({ name })} data-testid="settings-name-save">Save</Button></div>
      </div>
      <div className="card space-y-4 p-6">
        <h3 className="font-semibold">Email preferences</h3>
        {PREFS.map(([k, label]) => (
          <div key={k} className="flex items-center justify-between gap-4"><span className="text-sm">{label}</span>
            <Switch checked={prefs[k] !== false} onCheckedChange={(v) => save({ notification_prefs: { ...prefs, [k]: v } })} data-testid={`pref-${k}`} /></div>
        ))}
        <p className="text-xs text-slate-500">Password reset and security emails are always sent.</p>
      </div>
      <form onSubmit={changePw} className="card space-y-4 p-6">
        <h3 className="font-semibold">Password</h3>
        <Field label="Current password (leave blank if you only use Google)" htmlFor="cpw"><Input id="cpw" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="settings-current-password" /></Field>
        <Field label="New password" htmlFor="npw2"><Input id="npw2" type="password" minLength={8} required value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="settings-new-password" /></Field>
        <Button type="submit" variant="outline" className="rounded-full" data-testid="settings-password-save">Update password</Button>
      </form>
      <div className="card space-y-3 p-6">
        <h3 className="font-semibold">Your data</h3>
        <Button variant="outline" className="rounded-full" onClick={exportData} data-testid="settings-export-button">Download my data (JSON)</Button>
      </div>
      <div className="card space-y-3 border-rose-200 p-6">
        <h3 className="font-semibold text-rose-700">Delete account</h3>
        <p className="text-sm text-slate-600">This permanently deletes your account and trips you own that have no other travelers. On other trips your name is replaced with "Former traveler". This can't be undone.</p>
        <div className="flex gap-3"><Input placeholder="Type DELETE" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="settings-delete-confirm" />
          <Button variant="destructive" className="rounded-full" disabled={confirm !== "DELETE"} onClick={del} data-testid="settings-delete-button">Delete</Button></div>
      </div>
    </div>
  );
}
